import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import type { Enquiry } from "features/Enquiries/types/enquiry.types";
import { EnquiryStatus } from "features/Enquiries/constants/enquiry.constants";

function isTerminalEnquiryStatus(status: string): boolean {
  return status === EnquiryStatus.CONVERTED || status === EnquiryStatus.CANCELLED;
}

type EnquiryActionsDropdownProps = {
  record: Enquiry;
  canReadEnquiry: boolean;
  canUpdateEnquiry: boolean;
  canDeleteEnquiry: boolean;
  canReadSiteVisit: boolean;
  canCreateSiteVisit: boolean;
  onDelete: (enquiry: Enquiry) => void;
  onAssign?: (enquiry: Enquiry) => void;
  onReassign?: (enquiry: Enquiry) => void;
};

export default function EnquiryActionsDropdown({
  record,
  canReadEnquiry,
  canUpdateEnquiry,
  canDeleteEnquiry,
  canReadSiteVisit,
  canCreateSiteVisit,
  onDelete,
  onAssign,
  onReassign,
}: EnquiryActionsDropdownProps) {
  const isTerminal = isTerminalEnquiryStatus(record.status);
  const canDelete = canDeleteEnquiry && record.status === EnquiryStatus.NEW;
  const canAddSiteVisit = canCreateSiteVisit && record.status === EnquiryStatus.NEW;
  const hasSiteVisitItems = canReadSiteVisit || canAddSiteVisit;
  const showAssignmentItems = canUpdateEnquiry && !isTerminal && (onAssign || onReassign);
  const hasMenuItems =
    canReadEnquiry || canUpdateEnquiry || hasSiteVisitItems || canDelete || showAssignmentItems;

  if (!hasMenuItems) {
    return <span className="text-muted small">—</span>;
  }

  const hasTopItems = canReadEnquiry || canUpdateEnquiry;

  return (
    <UncontrolledDropdown>
      <DropdownToggle
        type="button"
        className="btn btn-soft-secondary btn-sm"
        tag="button"
        aria-label="Enquiry actions"
      >
        <i className="ri-more-2-fill align-middle" />
      </DropdownToggle>
      <DropdownMenu container="body" end>
        {canReadEnquiry ? (
          <DropdownItem
            tag={Link}
            to={`/enquiry-management/enquiries/${record.id}`}
          >
            <i className="ri-eye-line align-bottom me-2 text-muted" />
            View
          </DropdownItem>
        ) : null}
        {canUpdateEnquiry ? (
          <DropdownItem
            tag={Link}
            to={`/enquiry-management/enquiries/edit/${record.id}`}
          >
            <i className="ri-pencil-line align-bottom me-2 text-muted" />
            Edit
          </DropdownItem>
        ) : null}
        {showAssignmentItems ? (
          <>
            {hasTopItems ? <DropdownItem divider /> : null}
            {onAssign ? (
              <DropdownItem onClick={() => onAssign(record)}>
                <i className="ri-user-follow-line align-bottom me-2 text-muted" />
                Assign user
              </DropdownItem>
            ) : null}
            {onReassign ? (
              <DropdownItem onClick={() => onReassign(record)}>
                <i className="ri-user-shared-line align-bottom me-2 text-muted" />
                Reassign
              </DropdownItem>
            ) : null}
          </>
        ) : null}
        {hasSiteVisitItems ? (
          <>
            {(hasTopItems || showAssignmentItems) ? <DropdownItem divider /> : null}
            {canReadSiteVisit ? (
              <DropdownItem
                tag={Link}
                to={`/enquiry-management/enquiries/${record.id}?tab=site-visits`}
              >
                <i className="ri-map-pin-line align-bottom me-2 text-muted" />
                Site Visits
              </DropdownItem>
            ) : null}
            {canAddSiteVisit ? (
              <DropdownItem
                tag={Link}
                to={`/enquiry-management/enquiries/${record.id}/site-visits/create`}
              >
                <i className="ri-map-pin-add-line align-bottom me-2 text-muted" />
                Add Site Visit
              </DropdownItem>
            ) : null}
          </>
        ) : null}
        {canDelete ? (
          <>
            {(hasTopItems || showAssignmentItems || hasSiteVisitItems) ? (
              <DropdownItem divider />
            ) : null}
            <DropdownItem className="text-danger" onClick={() => onDelete(record)}>
              <i className="ri-delete-bin-line align-bottom me-2" />
              Delete
            </DropdownItem>
          </>
        ) : null}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}
